import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { ConditionalLeaderboard, WaitingSpinner, AnswerFeedbackCard, QuestionFeedbackSection, TimerBar, GameEndedScreen } from '../shared';
import { ClueRevealHost } from '../shared';
import { useHostPlaying } from '../../hooks/useHostPlaying';
import { calculateTimeRemaining } from '../../utils/gameUtils';
import { getGameName } from '../../utils/gameUtils';
import { ALL_GAMES } from '../../config/games';
import { getClueIntervalMs } from '../../constants/gameConstants';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';

interface ClueQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  clues?: string[];
  timeLimit: number;
  clueIntervalMs?: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{ playerId: string; answer: string; responseTime: number; isCorrect: boolean; points: number }>;
  leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
}

interface WhatCouldItBePlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: ClueQuestion;
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

const WhatCouldItBePlayerDisplay: React.FC<WhatCouldItBePlayerDisplayProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const hostIsPlaying = useHostPlaying(roomState);
  const [currentQuestion, setCurrentQuestion] = useState<ClueQuestion | null>(roomState.currentQuestion || null);
  const [timeRemaining, setTimeRemaining] = useState(roomState.currentQuestion?.timeLimit ?? 0);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<{
    mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
    wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
    totalQuestions: number;
    averageScore: number;
  } | null>(null);
  const [myScore, setMyScore] = useState(0);
  const [answer, setAnswer] = useState('');
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [showInterestingFact, setShowInterestingFact] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState<string | null>(null);
  const [acceptedAnswers, setAcceptedAnswers] = useState<string[] | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);
  const previousQuestionIdRef = useRef<string | undefined>(roomState.currentQuestion?.questionId || roomState.currentQuestion?.id);

  const gameEndData = useGameEndScreenData(roomState);

  const resetQuestion = () => {
    setAnswer('');
    setAnswerSubmitted(false);
    setAnswerCorrect(null);
    setPointsEarned(0);
    setShowInterestingFact(false);
    setCorrectAnswer(null);
    setAcceptedAnswers(null);
    isSubmittingRef.current = false;
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // When server says game is finished (e.g. reconnection, or room-state before game-ended), show game end screen
  const roomGameState = roomState.gameState;
  useEffect(() => {
    if (roomGameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- gameEndData is derived from roomState; we only need to run when finished
  }, [roomGameState, gameEndData]);

  // Sync score from roomState
  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const me = roomState.players.find((p) => p.id === myPlayerId);
      if (me) {
        setMyScore(me.score);
      }
      if (me && roomState.currentQuestion) {
        setTimeRemaining(calculateTimeRemaining(roomState.currentQuestion, (roomState as any).questionStartTime));
      }
    }
  }, [roomState.players, myPlayerId, roomState.currentQuestion, (roomState as any).questionStartTime]);

  useEffect(() => {
    if (roomState.currentQuestion) {
      const newId = roomState.currentQuestion.questionId || roomState.currentQuestion.id;
      const isNew = newId !== previousQuestionIdRef.current;
      const serverTimeRemaining = calculateTimeRemaining(roomState.currentQuestion, (roomState as any).questionStartTime);
      if (isNew || !currentQuestion) {
        previousQuestionIdRef.current = newId;
        setCurrentQuestion(roomState.currentQuestion);
        setTimeRemaining(serverTimeRemaining);
        setQuestionStartTime(Date.now());
        if (isNew) resetQuestion();
      } else {
        setTimeRemaining(serverTimeRemaining);
      }
    }
  }, [roomState.currentQuestion, (roomState as any).questionStartTime, currentQuestion]);

  useEffect(() => {
    const handleQuestion = (data: ClueQuestion) => {
      const newId = data.questionId || data.id;
      if (newId !== previousQuestionIdRef.current) {
        previousQuestionIdRef.current = newId;
        setCurrentQuestion(data);
        setTimeRemaining(data.timeLimit);
        setQuestionStartTime(Date.now());
        resetQuestion();
      }
    };

    const handleAnswerReceived = (data: {
      isCorrect: boolean;
      points: number;
      correctAnswer?: string;
      acceptedAnswers?: string[];
    }) => {
      isSubmittingRef.current = false;
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      setPointsEarned(data.points);
      if (!data.isCorrect && data.correctAnswer) {
        setCorrectAnswer(data.correctAnswer);
        setAcceptedAnswers(data.acceptedAnswers ?? null);
      }
      setShowInterestingFact(false);
      setTimeout(() => setShowInterestingFact(true), 1500);
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: {
        mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
        wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
        totalQuestions: number;
        averageScore: number;
      };
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      resetQuestion();
    };

    socket.on('question', handleQuestion);
    socket.on('answer-received', handleAnswerReceived);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);
    return () => {
      socket.off('question', handleQuestion);
      socket.off('answer-received', handleAnswerReceived);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
    };
  }, [socket, myPlayerId]);

  useEffect(() => {
    if (currentQuestion && timeRemaining > 0 && !answerSubmitted) {
      const t = setInterval(() => setTimeRemaining((p) => (p <= 100 ? 0 : p - 100)), 100);
      return () => clearInterval(t);
    }
  }, [currentQuestion, timeRemaining, answerSubmitted]);

  const handleSubmitAnswer = () => {
    if (!answer.trim() || !currentQuestion || answerSubmitted || isSubmittingRef.current) return;
    const qId = currentQuestion.questionId || currentQuestion.id;
    if (!qId) return;
    isSubmittingRef.current = true;
    setAnswerSubmitted(true);
    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: qId,
      answer: answer.trim(),
      timestamp: Date.now(),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitAnswer();
    }
  };

  const gameDisplayName = getGameName(roomState.gameType);
  const game = ALL_GAMES.find((g) => g.id === roomState.gameType) || ALL_GAMES.find((g) => g.id === 'what-could-it-be');
  const gameIcon = game?.icon ?? '🔮';

  // Game ended screen
  if (gameEnded) {
    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        resetQuestion();
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    const displayLeaderboard = finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? []);
    const displayStatistics = gameStatistics ?? gameEndData?.gameStatistics ?? null;
    return (
      <GameEndedScreen
        gameIcon={gameIcon}
        finalLeaderboard={displayLeaderboard}
        myPlayerId={myPlayerId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        showYouSuffix
        statistics={displayStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  // Loading state
  if (!currentQuestion) {
    return (
      <div className="app">
        <div className="container">
          <h1 className="title">{gameDisplayName}</h1>
          <p className="subtitle">{t('gameplay.closeOrNot.getReady')}</p>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid rgba(248, 250, 252, 0.3)',
              borderTop: '4px solid #F8FAFC',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        leftButton={{ icon: HeaderIcons.home, onClick: () => onGoHome?.(), tooltip: t('gameplay.leaveGame') }}
        rightButton={{ icon: HeaderIcons.refresh, onClick: () => window.location.reload(), tooltip: t('common.tooltips.refreshPage') }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <span className="header-game-name" style={{ fontSize: '1rem' }}>{gameDisplayName}</span>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold', padding: '0.3rem 0.6rem', backgroundColor: 'rgba(248,250,252,0.1)', borderRadius: '6px', border: '2px solid rgba(248,250,252,0.3)' }}>
              {roomState.roomName}
            </span>
          </div>
        }
      />
      <div className="container player-content-wrapper" style={{ paddingTop: '3rem', width: '100%', maxWidth: '100%' }}>
        {!answerSubmitted ? (
          <>
            <ConditionalLeaderboard hostIsPlaying={hostIsPlaying} players={roomState.players} myPlayerId={myPlayerId} />
            <TimerBar
              timeRemaining={timeRemaining}
              timeLimit={currentQuestion.timeLimit}
              questionNumber={currentQuestion.questionNumber}
              totalQuestions={currentQuestion.totalQuestions}
              showTimeCounter={false}
            />
            <div className="question-container" style={{ marginBottom: '1rem' }}>
              <h2 className="title" style={{ fontSize: '1.25rem', color: 'var(--text-primary)' }}>{currentQuestion.question}</h2>
            </div>
            {/* Clues only on player screen when host is playing (same as other games: host-only otherwise) */}
            {hostIsPlaying && (currentQuestion.clues?.length ?? 0) > 0 && (
              <ClueRevealHost clues={currentQuestion.clues!} startTime={questionStartTime} clueIntervalMs={currentQuestion.clueIntervalMs ?? getClueIntervalMs(currentQuestion.timeLimit)} />
            )}
            <div style={{ marginBottom: '1.5rem' }}>
              <input
                ref={inputRef}
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('gameplay.typeYourAnswer')}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '1.2rem',
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.08) 0%, rgba(var(--color-secondary-rgb), 0.14) 100%)',
                  border: '3px solid rgba(var(--color-secondary-rgb), 0.28)',
                  borderRadius: '15px',
                  color: 'var(--text-primary)',
                  outline: 'none',
                }}
              />
            </div>
            <button
              onClick={handleSubmitAnswer}
              disabled={!answer.trim()}
              className="button player-answer-submit-button"
              style={{
                width: '100%',
              }}
            >
              {t('gameplay.submitAnswer')}
            </button>
          </>
        ) : (
          <div style={{
            width: '100%',
            minHeight: 'calc(100dvh - 200px)',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
          }}>
            <ConditionalLeaderboard hostIsPlaying={hostIsPlaying} players={roomState.players} myPlayerId={myPlayerId} />
            {answerCorrect !== null && (
              <AnswerFeedbackCard
                isCorrect={answerCorrect}
                showDidYouKnow={showInterestingFact}
                interestingFact={currentQuestion?.interesting}
                pointsEarned={pointsEarned}
                correctAnswer={correctAnswer ?? undefined}
                acceptedAnswers={acceptedAnswers ?? undefined}
              />
            )}
            <div className="wcib-player-loading-spinner-wrap">
              <WaitingSpinner isLoading={false} />
            </div>
            {answerCorrect !== null && currentQuestion && (
              <div style={{ marginTop: 'auto', width: '100%' }}>
                <QuestionFeedbackSection
                  questionId={currentQuestion.questionId || currentQuestion.id || ''}
                  roomName={roomState.roomName}
                  gameType={roomState.gameType}
                  answerSubmitted={answerSubmitted}
                  showFeedback
                  onSubmitFeedback={(rating, correct, questionText, correctAnswerVal) => {
                    if ((rating !== undefined || correct !== undefined) && socket) {
                      socket.emit('question-feedback', {
                        roomName: roomState.roomName,
                        questionId: currentQuestion.questionId || currentQuestion.id,
                        gameType: roomState.gameType,
                        rating: rating ?? undefined,
                        correct: correct ?? undefined,
                        questionText: questionText ?? currentQuestion.question,
                        correctAnswer: correctAnswerVal ?? correctAnswer ?? undefined,
                      });
                    }
                  }}
                  questionText={currentQuestion.question}
                  correctAnswer={correctAnswer ?? undefined}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatCouldItBePlayerDisplay;
